import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Nav, Card, Modal } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import { useQueryClient } from "@tanstack/react-query";
import Avatar from "../components/Avatar";
import { searchCompanyInfo } from "../apis/CompanyInfo";
import { Logout, DeleteAccount } from "../apis/Users.js";
import { setLoggedOutAuthSession, useAuthSession } from "../apis/Auth";
import { clearGuestAccess } from "../utils/guestAccess";

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <Link
    to=""
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="dropdown-link"
  >
    {children}
  </Link>
));

export default function HeaderControls({
  isMobileSearchOpen,
  setIsMobileSearchOpen,
  skinModeState,
  toggleSkinMode,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authQuery = useAuthSession();
  // ----------------------------------------------------------------------
  // Search UI State
  // ----------------------------------------------------------------------
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearchCompleted, setHasSearchCompleted] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const [focusedTabId, setFocusedTabId] = useState("mypage");
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // ----------------------------------------------------------------------
  // Auth State
  // ----------------------------------------------------------------------
  const profileLoading = authQuery.isLoading;
  const isAuthenticated = authQuery.data?.authenticated || false;
  const profileName = authQuery.data?.profileName || "";
  const authUserId = authQuery.data?.payload?.user_id || null;

  const showSearchResults = useMemo(() => {
    if (!isSearchFocused) return false;
    if (!searchKeyword.trim()) return false;
    return isSearching || hasSearchCompleted;
  }, [hasSearchCompleted, isSearchFocused, isSearching, searchKeyword]);

  const profileAvatarText = useMemo(() => {
    const trimmedName = profileName.trim();
    if (!trimmedName) return "";
    return trimmedName.slice(0, 1);
  }, [profileName]);

  // 외부 클릭 시 검색 닫기
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!searchBoxRef.current?.contains(event.target)) {
        setIsSearchFocused(false);
        setIsMobileSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [setIsMobileSearchOpen]);

  // 회사 검색어 디바운스
  useEffect(() => {
    const trimmedKeyword = searchKeyword.trim();
    if (!trimmedKeyword) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setActiveResultIndex(-1);
      setIsSearching(false);
      setHasSearchCompleted(false);
      return undefined;
    }

    const controller = new AbortController();
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      setHasSearchCompleted(false);
      setSearchResults([]);
      setActiveResultIndex(-1);

      searchCompanyInfo(trimmedKeyword, 10, { signal: controller.signal })
        .then((items) => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchResults(items.slice(0, 10));
          setActiveResultIndex(-1);
          setHasSearchCompleted(true);
        })
        .catch((error) => {
          if (searchRequestIdRef.current !== requestId) return;
          if (error.name !== "AbortError") {
            setSearchResults([]);
            setHasSearchCompleted(true);
          }
        })
        .finally(() => {
          if (searchRequestIdRef.current !== requestId) return;
          setIsSearching(false);
        });
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchKeyword]);

  // 경로 변경 시 모바일 검색창 닫기
  useEffect(() => {
    setIsMobileSearchOpen(false);
  }, [setIsMobileSearchOpen]);

  useEffect(() => {
    if (isAuthenticated) {
      clearGuestAccess();
    }
  }, [isAuthenticated]);

  const handleMobileSearchToggle = useCallback(() => {
    if (isMobileSearchOpen) {
      setIsMobileSearchOpen(false);
      setIsSearchFocused(false);
      searchInputRef.current?.blur();
      return;
    }
    setIsMobileSearchOpen(true);
    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [isMobileSearchOpen, setIsMobileSearchOpen]);

  const handleStockMove = useCallback((item) => {
    const ticker = item?.ticker;
    if (!ticker) return;
    setSearchKeyword("");
    setSearchResults([]);
    setActiveResultIndex(-1);
    setHasSearchCompleted(false);
    setIsSearchFocused(false);
    setIsMobileSearchOpen(false);
    searchInputRef.current?.blur();
    navigate(`/stock/${ticker}`);
  }, [navigate, setIsMobileSearchOpen]);

  const handleSearchChange = useCallback((event) => {
    setIsSearchFocused(true);
    setSearchKeyword(event.target.value);
  }, []);

  const handleSearchKeyDown = useCallback((event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveResultIndex((prev) => {
        if (searchResults.length === 0) return -1;
        return prev >= searchResults.length - 1 ? 0 : prev + 1;
      });
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveResultIndex((prev) => {
        if (searchResults.length === 0) return -1;
        return prev <= 0 ? searchResults.length - 1 : prev - 1;
      });
      return;
    }
    if (event.key === "Escape") {
      setIsSearchFocused(false);
      setIsMobileSearchOpen(false);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (activeResultIndex >= 0 && activeResultIndex < searchResults.length) {
        handleStockMove(searchResults[activeResultIndex]);
        return;
      }
      if (searchResults.length > 0) {
        handleStockMove(searchResults[0]);
      }
    }
  }, [activeResultIndex, handleStockMove, searchResults, setIsMobileSearchOpen]);

  const handleLogout = useCallback(async () => {
    clearGuestAccess();
    setLoggedOutAuthSession(queryClient);
    try {
      await Logout();
    } catch (error) {
    } finally {
      navigate("/", {
        replace: true,
        state: { guestResetAt: Date.now() },
      });
    }
  }, [navigate, queryClient]);

  const handleWithdrawModalOpen = useCallback(() => {
    setIsDeleted(false);
    setShowWithdrawModal(true);
  }, []);

  const handleWithdrawModalClose = useCallback(() => {
    setIsDeleted(false);
    setShowWithdrawModal(false);
  }, []);

  const handleWithdrawNoticeChange = useCallback((event) => {
    setIsDeleted(event.target.checked);
  }, []);

  // 회원탈퇴 처리
  const handleAccountDelete = useCallback(async () => {
    try {
      await DeleteAccount(authUserId, isDeleted);
    } catch (error) {
    } finally {
      setIsDeleted(false);
      setShowWithdrawModal(false);
      setLoggedOutAuthSession(queryClient);
      alert("회원 탈퇴가 완료되었습니다.");
      navigate("/", {
        replace: true,
        state: { guestResetAt: Date.now() },
      });
    }
  }, [authUserId, navigate, queryClient, isDeleted]);

  return (
    <div className="header-main-right d-flex align-items-center gap-2 ms-auto justify-content-end flex-grow-1">
      {/* Mobile Search Toggle Button */}
      <button
        type="button"
        onClick={handleMobileSearchToggle}
        className="header-mobile-search-btn d-md-none"
        aria-label={isMobileSearchOpen ? "검색 닫기" : "검색 열기"}
        aria-expanded={isMobileSearchOpen}
      >
        <i className={isMobileSearchOpen ? "ri-close-line" : "ri-search-line"}></i>
      </button>

      {/* Main Search Input Box */}
      <div ref={searchBoxRef} className={`form-search ${isSearchFocused ? "onfocus" : ""}`}>
        <input
          ref={searchInputRef}
          type="text"
          className="form-control px-3"
          placeholder="회사명 또는 티커 검색"
          value={searchKeyword}
          onFocus={() => {
            setIsSearchFocused(true);
            if (window.matchMedia("(max-width: 575.98px)").matches) {
              setIsMobileSearchOpen(true);
            }
          }}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
        />
        <i className="ri-search-line position-absolute top-50 end-0 translate-middle-y me-2 opacity-50"></i>

        {/* Search Suggestion Dropdown (Bootstrapped rendering) */}
        {showSearchResults && (
          <div
            className="search-suggest-dropdown shadow-sm border border-gray-300 rounded mt-2 position-absolute top-100 w-100 start-0"
            style={{ zIndex: 1200, maxHeight: "320px", overflowY: "auto", overflowX: "hidden" }}
          >
            {isSearching ? (
              <div className="p-2 text-secondary small px-3">검색 중...</div>
            ) : searchResults.length === 0 ? (
              <div className="p-2 text-secondary small px-3">검색 결과가 없습니다.</div>
            ) : (
              searchResults.map((item, index) => {
                const companyName = item?.companykor || item?.companyKor || item?.company || "-";
                const ticker = item?.ticker || "-";
                const nickname = item?.nickname || "-";

                // 다크모드/라이트모드 및 active 상태에 따른 배경색/글자색 조합
                const bgClass = skinModeState === "dark"
                  ? (index === activeResultIndex ? "bg-secondary text-white" : "bg-dark text-white")
                  : (index === activeResultIndex ? "bg-light text-dark" : "bg-white text-dark");

                return (
                  <button
                    key={`${ticker}-${index}`}
                    type="button"
                    className={`btn w-100 text-start px-3 py-2 border-0 border-bottom rounded-0 ${bgClass}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleStockMove(item)}
                    style={{ transition: "background-color 0.2s" }}
                  >
                    <span className="d-block fw-bold" style={{ fontSize: "13px", color: "inherit" }}>{companyName} / {ticker}</span>
                    <span className="d-block mt-1" style={{ fontSize: "11px", color: "inherit", opacity: 0.7 }}>{nickname}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Skin Toggle (Re-coded as standard Link instead of Dropdown) */}
      <Link
        to=""
        className="dropdown-link dropdown-skin d-flex align-items-center justify-content-center flex-shrink-0 ms-2 me-1"
        onClick={(e) => {
          e.preventDefault();
          toggleSkinMode();
        }}
        aria-label="스킨 모드 변경"
        style={{ width: "34px", height: "34px", borderRadius: "10px" }}
      >
        <i className={skinModeState === "dark" ? "ri-moon-clear-line text-secondary" : "ri-sun-line text-primary"} style={{ fontSize: "22px" }}></i>
      </Link>

      {/* User Login/Profile Section */}
      {!profileLoading && isAuthenticated ? (
        <Dropdown className="dropdown-profile" align="end">
          <Dropdown.Toggle as={CustomToggle}>
            <div className="d-flex align-items-center gap-2" aria-label={`로그인 사용자 ${profileName || "회원"}`}>
              <Avatar initial={profileAvatarText} size="sm" />
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu className="mt-10-f p-0 shadow border-0" style={{ width: "230px" }}>
            <Card>
              <Card.Header className="p-2fw-bold">
                내 정보
              </Card.Header>
              <Card.Body className="p-2">
                <Nav className="nav-line">
                  {
                    [{ key: "mypage", label: "계정 설정" }].map((item) => {
                      const isActive = item.key === focusedTabId;
                      return (

                        <Nav.Link key={item.key} as="button" type="button" className={`dropdown-link ${isActive ? "active" : ""}`} onClick={() => { setFocusedTabId(item.key); }}>
                          {item.label}
                        </Nav.Link>
                      )
                    })
                  }
                </Nav>
                <Card className="border-0 shadow-none mt-1 mb-1">
                  <Card.Body className="p-1">
                    <div className="dropdown-menu-body">
                      <Form.Label className="text-secondary fs-xs mb-1">닉네임</Form.Label>
                      <Form.Control type="text" readOnly value={profileName || "회원"} className="text-secondary px-2 py-1 rounded border bg-secondary bg-opacity-10" style={{ fontSize: "12px" }} />
                    </div>
                    <Form.Label className="text-secondary fs-xs mb-1 mt-2" >이메일</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={authQuery.data?.payload.email === null ? "등록된 이메일이 없습니다." : authQuery.data?.payload.email}
                      className="text-secondary mt-2 px-2 py-1 rounded border bg-secondary bg-opacity-10"
                      style={{ fontSize: "12px" }}
                    />
                  </Card.Body>
                </Card>
                <div className="d-flex justify-content-between align-items-center mt-2 gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm rounded-pill px-3 fw-semibold"
                    onClick={handleLogout}
                  >
                    로그아웃
                  </button>
                  <button
                    type="button"
                    className="btn btn-link text-secondary py-1 px-0 text-decoration-none"
                    onClick={handleWithdrawModalOpen}
                  >
                    회원 탈퇴
                  </button>
                </div>

              </Card.Body>
            </Card>
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <Link to="/pages/signin" className="btn btn-primary btn-sm rounded-pill px-3 fw-semibold text-nowrap flex-shrink-0" style={{ fontSize: "12px" }}>
          로그인
        </Link>
      )}

      {/* 회원 탈퇴 모달 */}
      <Modal show={showWithdrawModal} onHide={handleWithdrawModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>회원 탈퇴</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-2">
            <div className="fw-bold text-danger" style={{fontSize: "16px"}}>정말 탈퇴하시겠습니까?</div>
            <div className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.6 }}>
            - 회원 정보 및 서비스 이용 기록은 모두 삭제됩니다.
            </div>
            <div className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.6 }}>
            - 삭제된 데이터는 복구할 수 없습니다.
            </div>
            <div className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.6 }}>
            - 관련 법령에 따라 일부 정보는 최대 5일 동안 보관될 수 있습니다.
            </div>
            <div className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.6 }}>
            - 탈퇴 후 동일 계정으로 재가입이 제한될 수 있습니다.
            </div>
            <Form.Check
              id="withdraw-notice-check"
              type="checkbox"
              className="mt-2 text-primary"
              label="위 내용을 모두 확인했습니다."
              checked={isDeleted}
              onChange={handleWithdrawNoticeChange}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          {isDeleted && (
            <button
              type="button"
              className="btn btn-primary btn-sm rounded-pill px-3 fw-semibold"
              onClick={handleAccountDelete}
            >
              확인
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
